// MaturaPowerFactorOutClosedTask.js
// Zadanie maturalne zamkniete:
// (a^n + a^(n+1) + a^(n+2)) / a^n

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function uniqueStrings(values, needed, avoid) {
  const out = [];
  const used = new Set([avoid]);
  for (const v of values) {
    const s = String(v);
    if (used.has(s)) continue;
    used.add(s);
    out.push(s);
    if (out.length >= needed) break;
  }
  return out;
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaPowerFactorOutClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 2,
    },
    data: {
      base: 5,
      n: 12,
      exprLatex: "\\dfrac{5^{12}+5^{13}+5^{14}}{5^{12}}",
      correctValue: 31,
      options: [
        { label: "A", latex: "30", isCorrect: false },
        { label: "B", latex: "31", isCorrect: true },
        { label: "C", latex: "5^{12}", isCorrect: false },
        { label: "D", latex: "5^{27}", isCorrect: false },
      ],
      correctIndex: 1,
      maxPoints: 1,
    },
  };
}

export function generateTask(generateOptions = {}) {
  if (
    generateOptions.variant === "original" &&
    generateOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  // Zgodnie z Twoja prosba: unikamy baz 4, 8, 9.
  const base = pick([2, 3, 5, 6, 7]);
  const n = pick([8, 9, 10, 11, 12, 13, 14]);

  const correctValue = 1 + base + base * base;

  const exprLatex = `\\dfrac{${base}^{${n}}+${base}^{${n + 1}}+${base}^{${n + 2}}}{${base}^{${n}}}`;

  // "Maturalne" zmylki:
  // - blisko wyniku liczbowego
  // - zostawienie potegi
  // - bledna algebra wykładnikow przy sumie poteg
  const distractorCandidates = [
    `${correctValue - 1}`,
    `${base}^{${n}}`,
    `${base}^{${2 * n + 3}}`,
  ];

  const wrong = uniqueStrings(distractorCandidates, 3, `${correctValue}`);

  const optionsRaw = [
    { latex: `${correctValue}`, isCorrect: true },
    ...wrong.map((w) => ({ latex: w, isCorrect: false })),
  ];

  const options = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    latex: o.latex,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaPowerFactorOutClosedTask",
    data: {
      base,
      n,
      exprLatex,
      correctValue,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math={data.exprLatex} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = data.options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { base, n, exprLatex, correctValue } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Wyciągamy przed nawias wspólny czynnik{" "}
        <InlineMath math={`${base}^{${n}}`} />:
      </div>
      <BlockMath
        math={`\\begin{aligned}${base}^{${n}}+${base}^{${n + 1}}+${base}^{${n + 2}}\\\\ = ${base}^{${n}}\\left(1+${base}+${base}^{2}\\right)\\end{aligned}`}
      />

      <div>
        <strong>Krok 2.</strong> Skracamy przez{" "}
        <InlineMath math={`${base}^{${n}}`} />:
      </div>
      <BlockMath
        math={`\\begin{aligned}&${exprLatex}\\\\ &= \\dfrac{${base}^{${n}}\\left(1+${base}+${base}^{2}\\right)}{${base}^{${n}}}\\\\ &= 1+${base}+${base}^{2}\\end{aligned}`}
      />

      <div>
        <strong>Krok 3.</strong> Obliczamy wartość liczbową:
      </div>
      <BlockMath math={`1+${base}+${base}^{2} = ${correctValue}`} />
    </div>
  );
}
